import { filter, firstValueFrom, map, Subject } from "rxjs";
import { Http } from "./http";
import { WebSocket } from "./websocket";
import { RTC } from "./rtc";
import { ID, Logger, MapND } from "@kosmod/kit";
import { Channel } from "./channel";

export const createMemoryNetwork = (maxLag: number = 0, minLag: number = 0, logger: Logger = Logger.noop()) => {
  const MAX_LAG_DURATION = maxLag - minLag;

  const httpServers = new Map<string, Http.Handler>();
  const wsServers = new Map<string, { handler: WebSocket.Handler, protocol: WebSocket.ProtocolNegotiation.Handler }>();

  const rtcPeers = new Map<ID<"connection", true>, Subject<MediaStreamTrack>>();

  type Delivery = { deliveredAt: number, deliver(): void, reason?: string };
  const deliveryQueue: Delivery[] = [];

  const delivery = (reason?: string) => {
    const lag = minLag + (Math.random() * MAX_LAG_DURATION);
    
    const entry = {
      deliveredAt: Date.now() + lag,
      deliver() {},
      reason
    }
    const promise = new Promise<void>(resolve => {
      entry.deliver = resolve;
    });
    deliveryQueue.push(entry);
    return promise;
  };

  const httpLogger = logger.createSubsystem('http');
  const wsLogger = logger.createSubsystem('ws');
  
  return {
    tick() {
      const now = Date.now();
      
      while (deliveryQueue.length > 0) {
        const next = deliveryQueue[0];
        if (next.deliveredAt > now)
          return;

        if (next.reason)
          logger.createSubsystem(next.reason).log("Delivered")
        next.deliver();
        deliveryQueue.shift();
      }
    },

    createHTTPServer(host: string, handler: Http.Handler) {
      return {
        start() {
          httpServers.set(host, handler);
        },
        close() {
          httpServers.delete(host);
        }
      }
    },
    createHTTPClient(): Http.Client {
      return {
        async request(url, options) {
          const server = httpServers.get(url.hostname);
          const query = Object.fromEntries(url.searchParams.entries());
          const start = performance.now();
          await delivery();
          
          if (!server)
            throw new Error(`HTTP Host "${url.hostname}" not found in memory network.`)

          const request = {
            query,
            method: options.method || 'GET',
            path: url.pathname,
            body: options.body || null,
            headers: options.headers || {},
          };

          const response = await server(request)

          await delivery();
          const end = performance.now();
          const duration = end - start;
          httpLogger.createSubsystem(url.host).log(request.method, request.path, response.status, duration + 'ms')

          return response;
        }
      }
    },

    createWSServer(host: string, handler: WebSocket.Handler, protocol: WebSocket.ProtocolNegotiation.Handler = () => '') {
      return {
        start() {
          wsServers.set(host, { handler, protocol });
        },
        close() {
          wsServers.delete(host);
        }
      }
    },
    createWSClient(): WebSocket.Client {
      return {
        connect(url, protocols = []) {
          const server = wsServers.get(url.hostname);
          if (!server)
            throw new Error(`Websocket Host "${url.hostname}" not found in memory network.`)

          const query = Object.fromEntries(url.searchParams.entries());


          let disconnect = (_: number) => {};

          let isDisconnected = false;

          const disconnected = new Promise<{ reason: number }>((resolve => {
            disconnect = (reason: number) => {
              isDisconnected = true;
              resolve({ reason });
            }
          }));

          const serverReceive = new Subject<ArrayBuffer | string>();
          const clientReceive = new Subject<ArrayBuffer | string>();

          const serverPort = {
            async send(value: ArrayBuffer | string) {
              await delivery();
              clientReceive.next(value);
            },
            receive: serverReceive.pipe(filter(() => !isDisconnected))
          }
          const clientPort = {
            async send(value: ArrayBuffer | string) {
              await delivery();
              serverReceive.next(value);
            },
            receive: clientReceive.pipe(filter(() => !isDisconnected))
          }

          // the socket becomes ready 1ms after construction
          const ready = delivery()

          // negotiate the protocol
          const protocol = server.protocol({ path: url.pathname, query, protocols })

          const connectionLogger = wsLogger.createSubsystem(`${url.hostname}:${url.pathname}(${protocol})`)

          ready.then(() => connectionLogger.log(`Ready`))
          disconnected.then(() => connectionLogger.log(`Disconnected`))
          
          server.handler({
            path: url.pathname,
            query,
            disconnect,
            port: serverPort,
            ready,
            disconnected,
            protocol
          })

          return {
            port: clientPort,
            disconnect,
            ready: ready.then(() => ({ protocol })),
            disconnected,
            protocol
          }
        },
      }
    },

    createRTCClient(): RTC.Client {
      const negotiatePeer = async (
        signalingChannel: RTC.SignalChannelPort,
        address: ID<"connection", true>,
      ): Promise<Subject<MediaStreamTrack>> => {
        await delivery('negotiatePeer: Sending Offer');
        signalingChannel.send({ type: 'offer', sdp: 'magic:memory-network' });

        const remotePeerPromise = firstValueFrom(signalingChannel.receive.pipe(
          filter(x => x.type === 'candidate'),
          map(x => rtcPeers.get(x.info.candidate as ID<"connection", true>)),
          filter(x => !!x)
        ));

        const answer = await firstValueFrom(signalingChannel.receive.pipe(filter(x => x.type === 'answer')));

        if (answer.sdp !== 'magic:memory-network')
          throw new Error();

        await delivery('negotiatePeer: Sending Candidate');
        signalingChannel.send({ type: 'candidate', info: {
          candidate: address,
          sdpMid: null,
          sdpMLineIndex: null,
          usernameFragment: null
        } })
        
        logger.log('negotiatePeer: Waiting for Remote Peer Candidate')
        const remotePeer = await remotePeerPromise;

        logger.log('negotiatePeer: Got Peer')
        return remotePeer;
      };
      
      return {
        createPeer(signalingChannel) {
          const address = ID('connection');
          const onMedia = new Subject<MediaStreamTrack>();
          rtcPeers.set(address, onMedia);
          
          let remotePeer: null | Subject<MediaStreamTrack> = null;

          return {
            async sendMedia(track) {
              if (!remotePeer) {
                remotePeer = await negotiatePeer(signalingChannel, address);
              }
              remotePeer.next(track);
            },
            onMedia,
          }
        },
        async createPeerFromOffer(signalingChannel, message) {
          const address = ID('connection');
          const onMedia = new Subject<MediaStreamTrack>();

          rtcPeers.set(address, onMedia);

          if (message.sdp !== 'magic:memory-network')
            throw new Error();

          await delivery('createPeerFromOffer: Sending Answer');
          const remotePeerPromise = firstValueFrom(signalingChannel.receive.pipe(
            filter(x => x.type === 'candidate'),
            map(x => rtcPeers.get(x.info.candidate as ID<"connection", true>)),
            filter(x => !!x)
          ));
          signalingChannel.send({ type: 'answer', sdp: 'magic:memory-network' });

          await delivery(`createPeerFromOffer: Sending Candidate`);
          signalingChannel.send({ type: 'candidate', info: {
            candidate: address,
            sdpMid: null,
            sdpMLineIndex: null,
            usernameFragment: null
          } })
          logger.log('createPeerFromOffer: Waiting for Remote Peer Candidate')
          const remotePeer = await remotePeerPromise;
          logger.log('createPeerFromOffer: Got Peer')
          
          return {
            onMedia,
            sendMedia(track) {
              remotePeer.next(track);
            },
          };
        },
      }
    }
  }
};
